import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import type { TClaimMessage } from '@/@types/rcm';

import type { TGetNarrativeDev } from '../../modules/claims/freeClaims/narrativeDevTool/useGetGptResponse';
import { useDebounce } from '../../utils/hooks/useDebounce';
import { makeAPIRequestReactQueryWithHeaders } from '../api';
import { endpoints } from '../endpoints';

export interface InsuranceNarrativeResult {
  narrative: string;
  cached: boolean;
  devToolData: TGetNarrativeDev | undefined;
  traceId?: string; // Backend trace ID for Langfuse linking
}

export interface InsuranceNarrativeResultV3 {
  narrative: string;
  cached: boolean;
  traceId?: string | null;
  error?: string;
  messages: TClaimMessage[];
}

/**
 * Unified narrative hook that routes to v2 or v3 based on preference
 * Uses llmClaimNarrativeEnabled preference to determine which endpoint to call
 */
export const useGetNarrativeWithPreference = (
  treatmentIds: number[],
  unifiedAppointmentId?: number,
  estimatedVisitId?: number,
  imagingPatientVideaId?: string,
  useLlmNarrative?: boolean,
  options?: UseQueryOptions<
    InsuranceNarrativeResult | InsuranceNarrativeResultV3,
    unknown,
    InsuranceNarrativeResult | InsuranceNarrativeResultV3
  >,
) => {
  const {
    primaryPracticeId: currentPractice,
    primaryOrganizationId: currentOrg,
  } = useSelector(
    (state: {
      login: {
        user: {
          primaryPracticeId?: string;
          primaryOrganizationId?: string;
        };
      };
    }) => state.login.user,
  );

  const { debouncedValue: debouncedInput } = useDebounce(treatmentIds, 300);

  const path = useLlmNarrative
    ? `${endpoints.v3}/${endpoints.narrative}/${endpoints.insuranceSummary}`
    : `${endpoints.v2}/${endpoints.narrative}/${endpoints.insuranceSummary}`;

  const queryKey = useLlmNarrative
    ? [
        'narrative-v3',
        debouncedInput,
        currentOrg,
        currentPractice,
        estimatedVisitId,
        imagingPatientVideaId,
      ]
    : [
        'narrative-v2',
        debouncedInput,
        currentOrg,
        currentPractice,
        estimatedVisitId,
        imagingPatientVideaId,
      ];

  const isDebounceSettled =
    (debouncedInput?.length ?? 0) > 0 &&
    (treatmentIds?.length ?? 0) > 0 &&
    debouncedInput.length === treatmentIds.length;

  return useQuery<InsuranceNarrativeResult | InsuranceNarrativeResultV3>(
    queryKey,
    () =>
      makeAPIRequestReactQueryWithHeaders<
        InsuranceNarrativeResult | InsuranceNarrativeResultV3
      >(
        path,
        undefined,
        {
          Org: currentOrg,
          Practice: currentPractice,
        },
        'POST',
        {
          data: {
            ...(Number.isFinite(Number(estimatedVisitId))
              ? { estimatedVisitId: Number(estimatedVisitId) }
              : {}),
            ...(imagingPatientVideaId ? { imagingPatientVideaId } : {}),
            unifiedAppointmentId,
            treatmentIds: debouncedInput,
          },
        },
      ),
    {
      ...options,
      enabled: options?.enabled !== false && isDebounceSettled,
    },
  );
};
